/**
 * Sincronización y lectura de los catálogos que los formularios necesitan
 * cuando no hay red.
 *
 * Los formularios de carga (parte diario, lubricantes, combustible) no validan
 * nada contra el servidor: eligen de listas de claves foráneas (equipos, obras,
 * sectores, centros de costo) que hoy bajan por HTTP al montarse. En el campo
 * eso no está disponible, así que se baja todo de una cuando hay conexión y se
 * lee de acá.
 *
 * El bundle viene de un solo endpoint y no de una llamada por catálogo a
 * propósito: en una conexión mala, seis requests son seis oportunidades de
 * quedar a medias, y un catálogo que falta deja un desplegable vacío sin que
 * la app pueda distinguirlo de "no hay obras cargadas".
 */

import apiClient from '../services/apiClient';
import {
  guardarCatalogos,
  guardarMeta,
  leerCatalogo,
  leerMeta,
  limpiarDatosDeSesion,
} from './db';

/**
 * Catálogos que trae el bundle. El orden no importa; los nombres sí: son las
 * claves con las que se guardan en IndexedDB y las que devuelve el endpoint.
 *
 * **Un catálogo que no esté en esta lista no se guarda**, aunque el servidor lo
 * mande: la sincronización recorre esta lista, no la respuesta. Al sumar un
 * formulario nuevo hay que agregar acá sus catálogos, o sus desplegables van a
 * aparecer vacíos sin ningún error a la vista.
 */
export const CATALOGOS = [
  // Comunes.
  'equipos', 'obras', 'sectores', 'centros_costo',
  // Ficha del equipo sin señal (portal QR offline).
  'capacidades_llenado', 'repuestos_equipo', 'documentos_equipo',
  // Carga de lubricantes.
  'tareas_engrase', 'lubricantes', 'mecanicos',
  // Despacho de combustible.
  'lugares_despacho', 'cisternas', 'tipos_combustible',
];

/**
 * Los que el bundle entrega sin sesión — el mismo alcance que ya expone el
 * portal QR público. Tiene que coincidir con `PUBLICOS` de
 * `apps/taller/sync_offline.py`.
 */
export const CATALOGOS_PUBLICOS = [
  'equipos', 'obras', 'sectores', 'centros_costo',
  'capacidades_llenado', 'repuestos_equipo', 'documentos_equipo',
];

const META_SINCRONIZADO_EN = 'catalogos_sincronizados_en';
const META_TENANT = 'catalogos_tenant';
const META_USUARIO = 'catalogos_usuario';
const META_PERMISOS = 'catalogos_permisos';
const META_CATALOGOS_VERSION = 'catalogos_version';

/**
 * Versión de la **forma** de los catálogos, no de su contenido.
 *
 * Un teléfono con equipos ya descargados no vuelve a bajarlos solo: la app
 * sólo sincroniza sin que nadie toque nada cuando no hay ningún dato local
 * (ver `AppMovilLayout`). Si el backend empieza a mandar un campo nuevo en un
 * catálogo que ya se estaba sincronizando —como pasó al sumarle la ficha
 * técnica a `equipos`— ese teléfono se queda para siempre con la forma vieja
 * hasta que alguien note que falta algo y toque "Sincronizar" a mano.
 *
 * **Subir este número** cuando cambie la forma de un catálogo que ya viaja en
 * el bundle (campo nuevo, campo que cambia de significado). Cualquier
 * dispositivo con una versión guardada distinta —incluyendo uno que nunca la
 * guardó, porque este mecanismo no existía todavía— se resincroniza solo la
 * próxima vez que la app esté a la vista, sin que el usuario tenga que
 * enterarse de que había algo para actualizar.
 */
const CATALOGOS_VERSION = 3;

// ─── Sincronización ───────────────────────────────────────────────────────────

/**
 * Baja el bundle completo y lo guarda localmente. Requiere conexión.
 *
 * Devuelve `{ sincronizado_en, cantidades }`. Lanza si no hay red o si el
 * servidor rechaza — quien llama decide si eso es un error visible (el usuario
 * apretó "Sincronizar") o algo que se reintenta solo (arranque de la app).
 */
export async function sincronizarCatalogos() {
  const { data } = await apiClient.get('sync/catalogos/');

  // Cambio de tenant o de usuario: los catálogos guardados son de otra empresa
  // o de alguien con otros permisos y no se pueden mezclar. En producción cada
  // tenant tiene su subdominio y el navegador ya los aísla por origen, pero en
  // desarrollo todos comparten localhost y sin esto un tenant vería la flota
  // del otro.
  const tenantPrevio = await leerMeta(META_TENANT);
  const usuarioPrevio = await leerMeta(META_USUARIO);
  const tenantNuevo = data.tenant ?? null;
  const usuarioNuevo = data.usuario?.id ?? null;

  if ((tenantPrevio && tenantPrevio !== tenantNuevo) ||
      (usuarioPrevio && usuarioPrevio !== usuarioNuevo)) {
    await limpiarDatosDeSesion();
  }

  const porClave = {};
  const cantidades = {};
  for (const clave of CATALOGOS) {
    const datos = Array.isArray(data[clave]) ? data[clave] : [];
    porClave[clave] = datos;
    cantidades[clave] = datos.length;
  }

  await guardarCatalogos(porClave);

  const sincronizado_en = data.generado_en || new Date().toISOString();
  await guardarMeta(META_SINCRONIZADO_EN, sincronizado_en);
  await guardarMeta(META_TENANT, tenantNuevo);
  await guardarMeta(META_USUARIO, usuarioNuevo);
  // `permisos` en null significa superusuario (acceso total), igual que en el
  // resto de la app. Se guarda tal cual para no perder esa distinción.
  await guardarMeta(META_PERMISOS, data.permisos ?? null);
  // Marca con qué forma de catálogos quedó este dispositivo. Es lo que le
  // permite a `catalogosDesactualizados` avisar sin tener que comparar campo
  // por campo.
  await guardarMeta(META_CATALOGOS_VERSION, CATALOGOS_VERSION);

  return { sincronizado_en, cantidades };
}

/**
 * ¿La forma de los catálogos guardados quedó vieja respecto de lo que este
 * build de la app espera?
 *
 * Sin datos locales no hay nada que evaluar: ese caso ya lo resuelve el
 * arranque de `AppMovilLayout`, que sincroniza apenas detecta que no hay
 * nada descargado. Acá sólo importa el dispositivo que **ya** tiene equipos
 * guardados pero con una forma anterior — el caso que antes se quedaba sin
 * los campos nuevos hasta que alguien tocara "Sincronizar" a mano.
 */
export async function catalogosDesactualizados() {
  if (!(await hayDatosLocales())) return false;
  const version = await leerMeta(META_CATALOGOS_VERSION, null);
  return version !== CATALOGOS_VERSION;
}

/**
 * Revisa en segundo plano si los catálogos quedaron con una forma vieja y,
 * si es así, los vuelve a bajar sin que nadie tenga que pedirlo.
 *
 * Mismo patrón que `iniciarSincronizacionAutomatica` de `outbox.js`: revisa
 * al volver la red, al traer la app al frente y cada tanto — no sólo al
 * abrir la app de cero. Así un teléfono que quedó con la app abierta toda la
 * jornada, o que sólo la manda al fondo y la vuelve a traer sin llegar a
 * cerrarla, también se pone al día solo.
 */
export function iniciarVerificacionFrescura({ intervaloMs = 5 * 60000 } = {}) {
  let verificando = false;

  const verificar = async () => {
    if (verificando) return;
    verificando = true;
    try {
      if (await catalogosDesactualizados()) await sincronizarCatalogos();
    } catch {
      // Sin red o el servidor no contesta: se reintenta en el próximo disparo.
    } finally {
      verificando = false;
    }
  };

  const alVolverLaRed = () => verificar();
  const alVolverAlFrente = () => { if (document.visibilityState === 'visible') verificar(); };

  window.addEventListener('online', alVolverLaRed);
  document.addEventListener('visibilitychange', alVolverAlFrente);
  const temporizador = setInterval(verificar, intervaloMs);

  verificar();

  return () => {
    window.removeEventListener('online', alVolverLaRed);
    document.removeEventListener('visibilitychange', alVolverAlFrente);
    clearInterval(temporizador);
  };
}

/**
 * Deja el dispositivo en el estado de alguien sin sesión, al cerrarla.
 *
 * Dos cosas, y ninguna es opcional:
 *
 * - **Se vacía la grilla de permisos.** Si no, la que quedó de la sesión
 *   anterior sigue habilitando lubricantes y combustible en el inicio de la
 *   app. Quien agarre el teléfono después va a poder cargar algo que el
 *   servidor le va a rechazar al sincronizar, con el trabajo ya hecho.
 * - **Se borran sólo los catálogos privados.** Los públicos se dejan a
 *   propósito: son los mismos que el portal QR entrega sin cuenta, y sin ellos
 *   alguien que cierra sesión en medio del campo se queda sin la lista de
 *   equipos y la app deja de servirle para nada.
 *
 * La cola de envíos ni se toca: son cargas hechas en la obra que todavía no
 * llegaron. Ver `limpiarDatosDeSesion` en `db.js` para el mismo criterio.
 */
export async function degradarAAnonimo() {
  const privados = {};
  for (const clave of CATALOGOS) {
    if (!CATALOGOS_PUBLICOS.includes(clave)) privados[clave] = [];
  }
  await guardarCatalogos(privados);
  await guardarMeta(META_PERMISOS, []);
  await guardarMeta(META_USUARIO, null);
}

/** Fecha ISO de la última sincronización, o null si nunca se sincronizó. */
export function ultimaSincronizacion() {
  return leerMeta(META_SINCRONIZADO_EN, null);
}

/** True si ya hay catálogos usables sin red. */
export async function hayDatosLocales() {
  const equipos = await leerCatalogo('equipos');
  return equipos.length > 0;
}

// ─── Lectura ──────────────────────────────────────────────────────────────────

export function getEquipos() {
  return leerCatalogo('equipos');
}

export function getObras() {
  return leerCatalogo('obras');
}

export function getSectores() {
  return leerCatalogo('sectores');
}

export function getCentrosCosto() {
  return leerCatalogo('centros_costo');
}

// ─── Carga de lubricantes ─────────────────────────────────────────────────────

export function getTareasEngrase() {
  return leerCatalogo('tareas_engrase');
}

/** Fluidos de mantenimiento diario. El bundle ya viene acotado a esos. */
export function getLubricantes() {
  return leerCatalogo('lubricantes');
}

export function getMecanicos() {
  return leerCatalogo('mecanicos');
}

// ─── Despacho de combustible ──────────────────────────────────────────────────

export function getLugaresDespacho() {
  return leerCatalogo('lugares_despacho');
}

/** Cisternas; cada una trae `lugar_id` para poder filtrarlas por lugar. */
export function getCisternas() {
  return leerCatalogo('cisternas');
}

export function getTiposCombustible() {
  return leerCatalogo('tipos_combustible');
}

/** Un equipo por id, con la forma que espera el portal QR. */
export async function getEquipo(equipoId) {
  const equipos = await getEquipos();
  // Acepta tanto el id entero de siempre como el qr_token opaco de las
  // etiquetas nuevas (ver QrLabel.jsx) — el bundle offline trae los dos.
  return equipos.find((e) => String(e.id) === String(equipoId) || String(e.qr_token) === String(equipoId)) || null;
}

/** Busca por número de interno, sin distinguir mayúsculas ni espacios sobrantes. */
export async function getEquipoPorInterno(interno) {
  const buscado = String(interno || '').trim().toLowerCase();
  if (!buscado) return null;
  const equipos = await getEquipos();
  // El interno propio gana; el alternativo (UTE / proyecto) solo se prueba si no hay coincidencia.
  return equipos.find((e) => String(e.interno || '').trim().toLowerCase() === buscado)
    || equipos.find((e) => String(e.interno_alternativo || '').trim().toLowerCase() === buscado)
    || null;
}

/** Capacidades de llenado de un equipo, desde la copia local. */
export async function getCapacidadesDeEquipo(equipoId) {
  const todas = await leerCatalogo('capacidades_llenado');
  return todas.filter((c) => String(c.equipo_id) === String(equipoId));
}

/** Repuestos recomendados de un equipo, desde la copia local. */
export async function getRepuestosDeEquipo(equipoId) {
  const todos = await leerCatalogo('repuestos_equipo');
  return todos.filter((r) => String(r.equipo_id) === String(equipoId));
}

/**
 * Documentación de un equipo, desde la copia local — sólo tipo, fechas y
 * estado. No hay adjuntos: sin red no hay forma de descargarlos, así que ni
 * viajan en el bundle.
 */
export async function getDocumentosDeEquipo(equipoId) {
  const todos = await leerCatalogo('documentos_equipo');
  return todos.filter((d) => String(d.equipo_id) === String(equipoId));
}

/**
 * Ficha completa del equipo para el portal sin señal: datos, capacidades de
 * llenado, repuestos recomendados y documentación, en una sola lectura.
 */
export async function getFichaEquipo(equipoId) {
  const equipo = await getEquipo(equipoId);
  if (!equipo) return null;
  const [capacidades, repuestos, documentos] = await Promise.all([
    getCapacidadesDeEquipo(equipoId),
    getRepuestosDeEquipo(equipoId),
    getDocumentosDeEquipo(equipoId),
  ]);
  return {
    ...equipo,
    capacidades_llenado: capacidades,
    repuestos_recomendados: repuestos,
    documentos: documentos,
  };
}

/**
 * Arma para `equipoId` el mismo objeto que devuelve el endpoint `portal-qr`,
 * pero desde los datos locales.
 *
 * Que la forma coincida es lo que permite que el formulario no se entere de si
 * los datos vinieron de la red o del almacenamiento local — es la razón por la
 * que no hay que tocar el componente.
 */
export async function getEquipoParaPortal(equipoId) {
  const equipo = await getEquipo(equipoId);
  if (!equipo) return null;

  const [obras, sectores, centrosCosto] = await Promise.all([
    getObras(), getSectores(), getCentrosCosto(),
  ]);

  return { ...equipo, obras, sectores, centros_costo: centrosCosto };
}

// ─── Permisos ─────────────────────────────────────────────────────────────────

/**
 * Chequea un permiso contra la copia local de la grilla.
 *
 * Es UX, no seguridad: sirve para no ofrecerle a alguien un formulario que su
 * envío va a rebotar. El backend revalida siempre al recibir, y si el permiso
 * se revocó mientras la persona estaba sin red, el envío queda rechazado en la
 * bandeja con el motivo.
 *
 * El prefijo es `backend_taller`, el `label` real del AppConfig, que es con el
 * que Django emite los permisos y con el que los compara `DynamicModelPermission`
 * del lado del servidor. Mismo criterio que el `hasPermission` de la app web:
 * si acá aceptáramos un prefijo que el backend no reconoce, ofreceríamos un
 * formulario cuyo envío termina rebotando.
 *
 * **`undefined` y `null` no son lo mismo, y confundirlos falla abierto.**
 * `null` es el superusuario, igual que en `/api/auth/me/`. `undefined` es que
 * este dispositivo nunca sincronizó y no sabemos nada. Antes los dos caían en
 * el mismo `=== null` y devolvían `true`: un teléfono recién instalado ofrecía
 * lubricantes y combustible a cualquiera, incluso sin haber iniciado sesión,
 * porque todavía no había grilla que consultar. Sin saber, se responde que no.
 */
export async function tienePermisoLocal(modelo, accion) {
  const permisos = await leerMeta(META_PERMISOS, undefined);
  if (permisos === undefined) return false; // sin sincronizar: no sabemos
  if (permisos === null) return true; // superusuario: acceso total
  if (!Array.isArray(permisos)) return false;
  return permisos.includes(`backend_taller.${accion}_${modelo}`);
}

/**
 * ¿La grilla de permisos guardada localmente es la de este usuario?
 *
 * Compara contra quién trajo la última sincronización, no contra si hay una
 * sesión activa en memoria: es lo que permite notar un login (o un cambio de
 * cuenta) después de una sesión anónima. `degradarAAnonimo` deja
 * `catalogos_usuario` en `null` al cerrar sesión; si después alguien vuelve a
 * entrar y nadie vuelve a sincronizar, la grilla se queda vacía para siempre
 * aunque haya sesión — que es exactamente lo que le pasaba a mainadmin.
 */
export async function permisosSonDe(usuarioId) {
  const usuarioLocal = await leerMeta(META_USUARIO, null);
  return usuarioLocal === (usuarioId ?? null);
}
